import {useNavigate, useLocation} from "@/components/helpers/routerCompat";
import queryString from "query-string";
import {useSelectedAssetsDispatch} from "../../contexts/SelectedAssetsContext";
import qs from "qs";

const keys = ['tags', 'face_tags', 'auto_tags', 'hide_tags', 'hide_auto_tags', 'hide_face_tags', 'nsfw_detected', 'nsfw_cleared', 'auto_tagged', 'moderation_names', 'user_ids', 'exts', 'rights', 'taxonomy', 'hide_taxonomy', 'rights', 'rating', 'file_size', 'file_size_range', 'workflow_state', 'sql', 'q',
    'sort', 'order', 'creator_tag_id', 'unsaved_creator', 'no_creator', 'contracts', 'releases', 'expired', 'rights_code', 'has_people', 'has_gps', 'has_alt_text', 'published', 'created_at', 'captured_at', 'updated_at',
    'contract_asset_id', 'release_ids', 'submitted', 'bounds', 'aspect', 'proximity_word_1', 'proximity_word_2', 'proximity_max_gaps', 'proximity_field', 'type', 'unsubmitted', 'optimization_request', 'checkout', 'vote', 'aasm_state', 'custom_meta', 'block_level',
    'collection_ids', 'storage_folder_ids', 'lightbox_ids', 'selection', 'download_levels', 'watermark', 'frame_status', 'missing_captured_at', 'unidentified_faces', 'faces_indexed', 'faces_not_searched', 'organization_id', 'entity_type_id', 'metadata_entity_type_id', 'asset_spreadsheet_import_id', 'included_search_fields', 'search_empty_fields', 'search_full_fields', 'text_q', 'text_search_scope', 'text_search_matching', 'text_search_mode', 'auto_keywords_mode'];

const singleKeys = ['sql', 'q', 'sort', 'order', 'rating', 'contracts', 'releases', 'expired', 'has_people', 'has_gps', 'has_alt_text', 'published', 'file_size_range', 'auto_tagged', 'faces_indexed', 'nsfw_detected', 'nsfw_cleared',
    'captured_at', 'created_at', 'updated_at', 'submitted', 'bounds', 'aspect', 'proximity_word_1', 'proximity_word_2', 'proximity_max_gaps', 'proximity_field', 'type', 'unsubmitted', 'optimization_request', 'checkout',
    'no_creator', 'vote', 'aasm_state', 'custom_meta', 'block_level', 'collection_ids', 'storage_folder_ids', 'lightbox_ids', 'selection', 'watermark', 'frame_status', 'missing_captured_at', 'unidentified_faces', 'faces_not_searched', 'entity_type_id', 'metadata_entity_type_id', 'asset_spreadsheet_import_id', 'included_search_fields', 'search_empty_fields', 'search_full_fields', 'text_q', 'text_search_scope', 'text_search_matching', 'text_search_mode', 'auto_keywords_mode'];

const MAX_FILTER_QUERY_VALUES = 1000;
const arrayKeys = new Set([
    ...keys.filter(key => singleKeys.indexOf(key) === -1),
    'rating', 'captured_at', 'created_at', 'updated_at',
    'included_search_fields', 'search_empty_fields', 'search_full_fields'
]);
const scalarKeys = new Set(keys.filter(key => !arrayKeys.has(key) && key !== 'custom_meta'));
const stringifyOptions = {arrayFormat: 'indices'};

const appendStringValues = (target, value) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach(item => {
        if(typeof item === 'string' && target.length < MAX_FILTER_QUERY_VALUES) target.push(item);
    });
};

const normalizeFilterArray = value => {
    const normalized = [];

    if(Array.isArray(value) || typeof value === 'string') {
        appendStringValues(normalized, value);
        return normalized;
    }

    if(!value || typeof value !== 'object') return normalized;

    Object.entries(value)
        .map(([key, item]) => [Number(key), key, item])
        .filter(([index, key]) => Number.isSafeInteger(index) && index >= 0 && index < MAX_FILTER_QUERY_VALUES && String(index) === key)
        .sort(([left], [right]) => left - right)
        .forEach(([, , item]) => appendStringValues(normalized, item));

    return normalized;
};

const normalizeCustomMeta = value => {
    if(!value || typeof value !== 'object' || Array.isArray(value)) return {};

    return Object.entries(value).reduce((normalized, [field, fieldValue]) => {
        if(typeof fieldValue === 'string') {
            normalized[field] = fieldValue;
        } else {
            const values = normalizeFilterArray(fieldValue);
            if(values.length) normalized[field] = values;
        }
        return normalized;
    }, {});
};

const boundFilterParameters = search => {
    const query = search.replace(/^\?/, '');
    const scalarParameters = new Map();
    const repeatedScalarKeys = new Set();
    const structuredParameters = [];

    const collectParameter = parameter => {
        if(!parameter) return;

        const equalsIndex = parameter.indexOf('=');
        const encodedKey = equalsIndex === -1 ? parameter : parameter.slice(0, equalsIndex);
        let decodedKey;
        try {
            decodedKey = decodeURIComponent(encodedKey.replace(/\+/g, ' '));
        } catch {
            return;
        }
        const bracketIndex = decodedKey.indexOf('[');
        const topLevelKey = bracketIndex === -1 ? decodedKey : decodedKey.slice(0, bracketIndex);

        if(scalarKeys.has(topLevelKey)) {
            if(scalarParameters.has(topLevelKey)) {
                scalarParameters.delete(topLevelKey);
                repeatedScalarKeys.add(topLevelKey);
            } else if(!repeatedScalarKeys.has(topLevelKey)) {
                scalarParameters.set(topLevelKey, parameter);
            }
        } else if(structuredParameters.length < MAX_FILTER_QUERY_VALUES) {
            structuredParameters.push(parameter);
        }
    };

    let parameterStart = 0;
    for(let index = 0; index <= query.length; index += 1) {
        if(index === query.length || query[index] === '&') {
            collectParameter(query.slice(parameterStart, index));
            parameterStart = index + 1;
        }
    }

    return [...scalarParameters.values(), ...structuredParameters].join('&');
};

const parseFilterSearch = search => {
    // Parse brackets as objects first so numeric custom-meta names remain object keys.
    // Known array filters are compacted below without allocating sparse arrays.
    const parsed = qs.parse(boundFilterParameters(search), {
        parameterLimit: MAX_FILTER_QUERY_VALUES + scalarKeys.size,
        parseArrays: false
    });

    arrayKeys.forEach(key => {
        if(Object.prototype.hasOwnProperty.call(parsed, key)) parsed[key] = normalizeFilterArray(parsed[key]);
    });
    if(Object.prototype.hasOwnProperty.call(parsed, 'custom_meta')) parsed.custom_meta = normalizeCustomMeta(parsed.custom_meta);
    scalarKeys.forEach(key => {
        if(Object.prototype.hasOwnProperty.call(parsed, key) && typeof parsed[key] !== 'string') delete parsed[key];
    });

    return parsed;
};

// Keep the established indexed URL format used by existing shared and bookmarked searches.
const stringifyFilterSearch = filters => qs.stringify(filters, stringifyOptions);

const applyFilterAttrs = (filters, applied, attrs) => {
    let found;
    for(let key of singleKeys) {
        if(key in attrs) {
            filters[key] = attrs[key];
            found = true;
        }
    }

    if(!found) {
        Object.keys(attrs).forEach(key => {
            const values = Array.isArray(filters[key]) ? filters[key] : [];
            const attrValues = Array.isArray(attrs[key]) ? attrs[key] : [attrs[key]];
            filters[key] = applied
                ? values.filter(value => !attrValues.includes(value))
                : [...values, ...attrValues.filter(value => !values.includes(value))];
        });
    }

    return filters;
};

const clearFilterValues = (filters, onlyKey) => {
    // Ensure onlyKey isn't an event, in case `onClick={clearFilters}`:
    if(typeof onlyKey === 'string') delete filters[onlyKey];
    else keys.forEach(key => delete filters[key]);
    return filters;
};

function useFilters() {
    const navigate = useNavigate();
    let location = useLocation();

    const init = {}
    keys.map(key => {
        init[key] = singleKeys.indexOf(key) != -1 ? null : []
    })

    if(!init.rating) init.rating = []
    if(!init.captured_at) init.captured_at = []
    if(!init.created_at) init.created_at = []
    if(!init.updated_at) init.updated_at = []
    if(!init.workflow_state) init.workflow_state = []
    if(!init.download_levels) init.download_levels = []
    if(!init.organization_id) init.organization_id = []

    const parseFilters = ()=> {
        const filters = {...init, ...parseFilterSearch(window.location.search)}
        window.filters = filters
        return filters
    }

    const doSetFilters = (applied, attrs)=>{
        const filters = parseFilters()

        applyFilterAttrs(filters, applied, attrs)

        // Deep compact:
        const reducedFilters = Object.entries(filters).reduce((a,[k,v]) => (v == null ? a : (a[k]=v, a)), {})

        // Update search string to retain state:
        // if(location.hash != '#search') location.hash = null

        // Ensure path isn't changing:
        location.pathname = window.location.pathname
        location.search = stringifyFilterSearch(reducedFilters);//queryString.stringify(reducedFilters, {arrayFormat: 'bracket'})

        navigate(location)
    }

    const setFilters = (applied, attrs)=>{
        doSetFilters(applied, attrs)
    }

    const setFiltersKeepSelection = (applied, attrs)=>{
        doSetFilters(applied, attrs)
    }

    const clearFilters = onlyKey =>{
        const search = parseFilterSearch(location.search)

        clearFilterValues(search, onlyKey)

        location.search = stringifyFilterSearch(search);
        location.hash = null
        navigate(location)
    }

    const replaceFilters = (newFilters, path)=>{
        if(path) location.pathname = path;
        location.search = stringifyFilterSearch(newFilters)
        location.hash = null
        navigate(location)
    }

    const removeFilter = key => {
        clearFilters(key)
    }

    const filters = parseFilters()

    let filterCount = _.filter([
        Object.keys(filters.custom_meta || {}).length == 0,
        filters.rights.length == 0,
        filters.contract_asset_id.length == 0,
        filters.release_ids.length == 0,
        filters.user_ids.length == 0,
        filters.exts.length == 0,
        filters.rating.length == 0,
        filters.captured_at.length == 0,
        filters.created_at.length == 0,
        filters.updated_at.length == 0,
        filters.workflow_state.length == 0,
        !(filters.watermark && filters.watermark != ''),
        !(filters.block_level && filters.block_level != ''),
        !(filters.vote && filters.vote != ''),
        !(filters.aasm_state && filters.aasm_state != ''),
        !(filters.no_creator && filters.no_creator != ''),
        !(filters.unsaved_creator && filters.unsaved_creator != ''),
        !(filters.creator_tag_id && filters.creator_tag_id != ''),
        !(filters.file_size_range && filters.file_size_range != ''),
        !(filters.sql && filters.sql != ''),
        !(filters.q && filters.q != ''),
        !(filters.text_q && filters.text_q != ''),
        !(filters.contracts && filters.contracts != ''),
        !(filters.releases && filters.releases != ''),
        !(filters.expired && filters.expired != ''),
        !(filters.has_people && filters.has_people != ''),
        !(filters.unsubmitted && filters.unsubmitted != ''),
        !(filters.has_gps && filters.has_gps != ''),
        !(filters.has_alt_text && filters.has_alt_text != ''),
        !(filters.published && filters.published != ''),
        !(filters.auto_tagged && filters.auto_tagged != ''),
        !(filters.nsfw_detected && filters.nsfw_detected != ''),
        !(filters.nsfw_cleared && filters.nsfw_cleared != ''),
        !(filters.submitted && filters.submitted != ''),
        !(filters.bounds && filters.bounds != ''),
        !(filters.aspect && filters.aspect != ''),
        !(filters.proximity_word_1 && filters.proximity_word_1 != ''),
        !(filters.optimization_request && filters.optimization_request != ''),
        !(filters.checkout && filters.checkout != ''),
        !(filters.storage_folder_ids && filters.storage_folder_ids != ''),
        !(filters.collection_ids && filters.collection_ids != ''),
        !(filters.lightbox_ids && filters.lightbox_ids != ''),
        !(filters.download_levels && filters.download_levels != ''),
        !(filters.frame_status && filters.frame_status != ''),
        !(filters.entity_type_id && filters.entity_type_id != ''),
        !(filters.metadata_entity_type_id && filters.metadata_entity_type_id != ''),
        !(filters.asset_spreadsheet_import_id && filters.asset_spreadsheet_import_id != ''),
        !(filters.missing_captured_at && filters.missing_captured_at != ''),
        !(filters.unidentified_faces && filters.unidentified_faces != ''),
        !(filters.faces_not_searched && filters.faces_not_searched != ''),
    ], bool => !bool).length

    filterCount += filters.tags.length +
        filters.auto_tags.length +
        filters.face_tags.length +
        filters.hide_tags.length +
        filters.hide_auto_tags.length +
        filters.hide_face_tags.length +
        filters.moderation_names.length +
        filters.taxonomy.length +
        filters.hide_taxonomy.length +
        filters.rights_code.length +
        filters.organization_id.length +
        (filters.search_empty_fields || []).length +
        (filters.search_full_fields || []).length

    return {location, filters, setFilters, clearFilters, replaceFilters, setFiltersKeepSelection, removeFilter, filterCount};
}


export {useFilters, parseFilterSearch, stringifyFilterSearch, applyFilterAttrs, clearFilterValues}
